import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiTrustCreateWish, apiTrustQuote } from "../lib/api";

type WishType = "buy" | "sell";
type Asset = "USDT" | "USDC" | "BTC" | "ETH";

export default function TrustWishCreate() {
  const { id } = useParams<{ id: string }>();
  const trustId = Number(id);
  const nav = useNavigate();

  const [type, setType] = React.useState<WishType>("buy");
  const [asset, setAsset] = React.useState<Asset>("USDT");
  const [amount, setAmount] = React.useState<string>("");
  const [desc, setDesc] = React.useState("");
  const [err, setErr] = React.useState<string | null>(null);

  const [busyQuote, setBusyQuote] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  // Quote result from server
  const [quoteFinal, setQuoteFinal] = React.useState<number | null>(null);
  const [quoteRate, setQuoteRate] = React.useState<number | null>(null);

  const inputBase =
    "rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500";

  const parsedAmount = React.useMemo(() => {
    const v = Number((amount || "").toString().replace(",", "."));
    return Number.isFinite(v) ? v : 0;
  }, [amount]);

  // Compute final shown to the user:
  const finalAmount = React.useMemo(() => {
    if (parsedAmount <= 0) return 0;
    return quoteFinal ?? 0;
  }, [parsedAmount, quoteFinal]);

  const rateUsed = quoteRate ?? 0;

  // Debounced live quote
  React.useEffect(() => {
    let timer: number | undefined;

    async function run() {
      setErr(null);
      setBusyQuote(true);
      try {
        const res = await apiTrustQuote(trustId, {
          type,
          asset,
          amount: parsedAmount,
        });
        // Expecting backend JSON: { ok, final_amount, rate_used, ... }
        const fa = Number((res as any)?.final_amount ?? 0);
        const ru = Number((res as any)?.rate_used ?? 0);
        setQuoteFinal(Number.isFinite(fa) ? fa : 0);
        setQuoteRate(Number.isFinite(ru) ? ru : 0);
      } catch (e: any) {
        setQuoteFinal(null);
        setQuoteRate(null);
        if (parsedAmount > 0) setErr(e?.message || "Failed to fetch live rate.");
      } finally {
        setBusyQuote(false);
      }
    }

    if (trustId > 0 && parsedAmount > 0) {
      timer = window.setTimeout(run, 350) as unknown as number;
    } else {
      setQuoteFinal(null);
      setQuoteRate(null);
    }

    return () => {
      if (timer) window.clearTimeout(timer);
    };
  }, [trustId, type, asset, parsedAmount]);

  /** -----------------------------
   * Submit new Wish → then Verify
   * -----------------------------*/
  async function onSubmit() {
    setErr(null);
    if (!parsedAmount || parsedAmount <= 0) return setErr("Enter a valid amount");
    if (!desc.trim()) return setErr("Please add instructions");
    if (busyQuote) return setErr("Please wait for the live rate.");
    if (rateUsed <= 0) return setErr("Live rate is required before creating a wish.");

    setLoading(true);
    try {
      const res = await apiTrustCreateWish(trustId, {
        type,
        asset,
        amount: parsedAmount,
        description: desc,
        rate_used: rateUsed,
        final_amount: finalAmount > 0 ? finalAmount : undefined,
      });

      // unwrap response safely
      const data = (res as any)?.data ?? res;

      if (!data?.ok) {
        throw new Error(data?.error || "Failed to create wish");
      }

      const wishId = data?.wish_id ?? data?.id ?? data?.wish?.id;
      if (!wishId) throw new Error("Wish created but id missing in response");

      // ✅ IMPORTANT: basename="/app" → never include "/app" in SPA paths
      nav(`/trusts/wishes/${wishId}/verify`);
    } catch (e: any) {
      setErr(e?.message || "Error creating wish");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="section-box max-w-xl mx-auto p-4 sm:p-6">
      <h1 className="text-xl font-semibold mb-3">Create Wish</h1>
      {err && <div className="alert-danger mb-3">{err}</div>}

      <div className="space-y-4">
        {/* Type / Asset */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">Type</label>
            <select
              className={["input w-full", inputBase].join(" ")}
              value={type}
              onChange={(e) => setType(e.target.value as WishType)}
            >
              <option value="buy">Buy (EUR → CRYPTO)</option>
              <option value="sell">Sell (CRYPTO → EUR)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Asset</label>
            <select
              className={["input w-full", inputBase].join(" ")}
              value={asset}
              onChange={(e) => setAsset(e.target.value as Asset)}
            >
              <option>USDT</option>
              <option>USDC</option>
              <option>BTC</option>
              <option>ETH</option>
            </select>
          </div>
        </div>

        {/* Amount */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium mb-1">
              Amount ({type === "buy" ? "EUR" : asset})
            </label>
            <input
              className={["input w-full", inputBase].join(" ")}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              inputMode="decimal"
            />
          </div>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium mb-1">Instructions (mandatory)</label>
          <textarea
            className={["input w-full", inputBase, "min-h-[90px]"].join(" ")}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            placeholder="What your Trustee should know about your wish..."
          />
        </div>

        {/* Quote box */}
        <div className="card-box">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-gray-500">Final:</div>
              <div className="text-lg font-semibold">
                {finalAmount > 0 ? finalAmount.toLocaleString() : "—"}{" "}
                {type === "buy" ? asset : "EUR"}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs text-gray-500">
                Rate used: {rateUsed > 0 ? rateUsed : "—"}
              </div>
              {busyQuote && parsedRateInput <= 0 && (
                <div className="text-xs text-gray-400">Fetching live rate…</div>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-2">
          <button
            className="btn btn-success"
            onClick={onSubmit}
            disabled={loading || busyQuote || parsedAmount <= 0 || !desc.trim() || rateUsed <= 0}
          >
            {loading ? "Creating…" : "Create Wish"}
          </button>
        </div>
      </div>
    </div>
  );
}
